import { Card } from "@/components/ui/Card";
import { cn } from "@/lib/cn";
import { formatRelative } from "@/lib/format";
import { Brain, Plus, RotateCcw, Star } from "lucide-react";

// Structural memory shape (was Doc<"foodMemories">): the Library screen
// passes plain view objects in tests/demo, and Doc<"foodMemories"> remains
// assignable to this interface.
export interface FoodMemoryLike {
  readonly _id: string;
  readonly foodName: string;
  readonly servingSize?: string;
  readonly calories: number;
  readonly confidence: string;
  readonly source: string;
  readonly lastUsedAt: number;
  readonly favorite?: boolean;
}

export interface FoodMemoryCardProps {
  memory: FoodMemoryLike;
  onEdit: (memory: FoodMemoryLike) => void;
  // Optional Library row actions (todo 19). All stopPropagation so they never
  // trigger the card's edit-on-click.
  onLogAgain?: (memory: FoodMemoryLike) => void;
  onAddToPlate?: (memory: FoodMemoryLike) => void;
  onToggleFavorite?: (memory: FoodMemoryLike) => void;
}

const confidenceClasses: Record<string, string> = {
  high: "bg-accent-light text-accent",
  medium: "bg-warning/10 text-warning",
  low: "bg-muted text-text-muted",
};

const actionButtonClass = cn(
  "flex min-h-[44px] min-w-[44px] items-center justify-center rounded-full",
  "text-text-muted active:bg-muted active:scale-[0.98] select-none",
);

export function FoodMemoryCard({
  memory,
  onEdit,
  onLogAgain,
  onAddToPlate,
  onToggleFavorite,
}: FoodMemoryCardProps) {
  const favorite = memory.favorite ?? false;
  return (
    <Card
      data-testid={`memory-card-${memory._id}`}
      className={cn(
        "flex items-center justify-between gap-2 p-4",
        "transition-transform duration-200 ease-out",
        "active:scale-[0.98] select-none cursor-pointer",
      )}
      onClick={() => onEdit(memory)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onEdit(memory);
        }
      }}
    >
      <div className="flex items-center gap-3 min-w-0">
        <div className="rounded-full bg-accent-light p-2 text-accent shrink-0">
          <Brain size={20} strokeWidth={2} />
        </div>
        <div className="flex flex-col min-w-0">
          <span className="text-sm font-medium text-text truncate">
            {memory.foodName}
          </span>
          <span className="text-xs text-text-muted">
            {memory.servingSize ? `${memory.servingSize} · ` : ""}
            {memory.calories} kcal
          </span>
          <span className="text-xs text-text-muted">
            {formatRelative(memory.lastUsedAt)} · {memory.source}
          </span>
        </div>
      </div>
      <div className="flex items-center gap-1 shrink-0">
        {onLogAgain !== undefined && (
          <button
            type="button"
            data-testid={`memory-log-again-${memory._id}`}
            aria-label={`Log ${memory.foodName} again`}
            className={actionButtonClass}
            onClick={(e) => {
              e.stopPropagation();
              onLogAgain(memory);
            }}
          >
            <RotateCcw size={18} />
          </button>
        )}
        {onAddToPlate !== undefined && (
          <button
            type="button"
            data-testid={`memory-add-plate-${memory._id}`}
            aria-label={`Add ${memory.foodName} to plate`}
            className={actionButtonClass}
            onClick={(e) => {
              e.stopPropagation();
              onAddToPlate(memory);
            }}
          >
            <Plus size={18} />
          </button>
        )}
        {onToggleFavorite !== undefined && (
          <button
            type="button"
            data-testid={`memory-fav-${memory._id}`}
            aria-label={favorite ? `Unfavourite ${memory.foodName}` : `Favourite ${memory.foodName}`}
            aria-pressed={favorite}
            className={cn(actionButtonClass, favorite && "text-warning")}
            onClick={(e) => {
              e.stopPropagation();
              onToggleFavorite(memory);
            }}
          >
            <Star size={18} fill={favorite ? "currentColor" : "none"} />
          </button>
        )}
        <span
          className={cn(
            "rounded-full px-2 py-0.5 text-xs font-medium uppercase shrink-0",
            confidenceClasses[memory.confidence] ?? confidenceClasses.low,
          )}
        >
          {memory.confidence}
        </span>
      </div>
    </Card>
  );
}
